<template>
    <div class="box has-text-centered py-6">
        <h3 class="title is-4 mb-3">{{ translate("page.category_manager.extraction_in_progress_title") }}</h3>

        <p class="subtitle is-6 mb-5">
            {{ translate("page.category_manager.extraction_in_progress_description") }}
        </p>

        <progress class="progress is-info mb-4" :value="isIndeterminate && !isCompleting ? undefined : progress" max="100"></progress>

        <p class="has-text-grey mb-2">{{ translate("page.category_manager.extraction_in_progress_hint") }}</p>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted } from "vue";
    import { translate } from "../../Common/translations";
    import { useCategoriesManagerStore } from "../categoriesManagerStore";
    import categoriesExtractionApi from "../../Api/Categories/categoriesExtraction";
    import { handleError } from "../../Common/errorHandler";
    import { getElapsedSecondsFromUtc } from "../../Common/dateTimeUtils";

    const store = useCategoriesManagerStore();

    const AVERAGE_DURATION_SECONDS = 5 * 60; //Five mins.
    const BACKEND_STATUS_INTERVAL_MS = 5000;
    const FAST_PROGRESS_INTERVAL_MS = 10;

    const elapsedSeconds = ref(0);
    const isIndeterminate = ref(false);
    const isCompleting = ref(false);
    const displayProgress = ref(0);
    const isFinishing = ref(false);
    let progressIntervalId: ReturnType<typeof setInterval> | null = null;
    let backendStatusIntervalId: ReturnType<typeof setInterval> | null = null;
    let fastProgressIntervalId: ReturnType<typeof setInterval> | null = null;

    const progress = computed(() => {
        if (isCompleting.value) {
            return displayProgress.value;
        }
        return Math.min(Math.round((elapsedSeconds.value / AVERAGE_DURATION_SECONDS) * 100), 99);
    });

    function finishAndNavigate() {
        if (isFinishing.value) {
            return;
        }
        isFinishing.value = true;

        store.currentExtractionId = null;
        store.refreshExtractions();
    }

    function startFastProgress() {
        if (fastProgressIntervalId !== null || isFinishing.value) {
            return;
        }

        isCompleting.value = true;
        displayProgress.value = Math.min(Math.round((elapsedSeconds.value / AVERAGE_DURATION_SECONDS) * 100), 99);

        fastProgressIntervalId = setInterval(() => {
            if (displayProgress.value >= 100) {
                stopPolling();
                return;
            }

            displayProgress.value++;

            if (displayProgress.value >= 100) {
                stopPolling();
                finishAndNavigate();
            }
        }, FAST_PROGRESS_INTERVAL_MS);
    }

    function pollBackendStatus() {
        if (!store.currentExtractionId) {
            return;
        }

        return categoriesExtractionApi
            .getExtraction(store.eventId, store.currentExtractionId)
            .then(extraction => {
                if (extraction.status === "done" || extraction.status === "failed") {
                    if (backendStatusIntervalId) {
                        clearInterval(backendStatusIntervalId);
                        backendStatusIntervalId = null;
                    }
                    if (progressIntervalId) {
                        clearInterval(progressIntervalId);
                        progressIntervalId = null;
                    }
                    startFastProgress();
                }
            })
            .catch(() => {
                // Silently ignore polling errors, will retry on next interval
            });
    }

    function stopPolling() {
        if (progressIntervalId) {
            clearInterval(progressIntervalId);
            progressIntervalId = null;
        }
        if (backendStatusIntervalId) {
            clearInterval(backendStatusIntervalId);
            backendStatusIntervalId = null;
        }
        if (fastProgressIntervalId) {
            clearInterval(fastProgressIntervalId);
            fastProgressIntervalId = null;
        }
    }

    onMounted(() => {
        // First, fetch the extraction to get the creation date
        if (!store.currentExtractionId) {
            return;
        }

        return categoriesExtractionApi
            .getExtraction(store.eventId, store.currentExtractionId)
            .then(extraction => {
                // Calculate elapsed time based on creation date
                const calculatedElapsedSeconds = getElapsedSecondsFromUtc(extraction.created);
                elapsedSeconds.value = calculatedElapsedSeconds;

                // Check if we should already be in indeterminate state
                if (calculatedElapsedSeconds >= AVERAGE_DURATION_SECONDS) {
                    isIndeterminate.value = true;
                }

                // Check if extraction is already done
                if (extraction.status === "done" || extraction.status === "failed") {
                    startFastProgress();
                    return;
                }

                // Start the progress interval
                progressIntervalId = setInterval(() => {
                    elapsedSeconds.value++;

                    if (progress.value >= 99 && !isCompleting.value) {
                        isIndeterminate.value = true;
                    }
                }, 1000);

                // Start polling
                backendStatusIntervalId = setInterval(pollBackendStatus, BACKEND_STATUS_INTERVAL_MS);
            })
            .catch(handleError);
    });

    onUnmounted(() => {
        stopPolling();
    });
</script>

<style scoped></style>
